import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getTerm } from '../data/termLibrary';
import { HelpCircle, X } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

// Contextual Tooltip for leadership terms
export function TermTooltip({ term, children }) {
  const termData = getTerm(term);
  
  if (!termData) return children;
  
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span 
            className="border-b border-dotted border-[#0055A5]/40 cursor-help hover:border-[#0055A5] transition-colors"
            data-testid={`term-${term.replace(/\s+/g, '-').toLowerCase()}`}
          >
            {children}
          </span>
        </TooltipTrigger>
        <TooltipContent 
          side="top" 
          className="bg-[#002B45] text-white border-none rounded-none p-4 max-w-xs shadow-2xl"
        >
          <p className="text-[10px] uppercase tracking-widest text-[#00A9E0] mb-2">
            {termData.category}
          </p>
          <p className="text-sm font-medium mb-1">{termData.term}</p>
          <p className="text-xs text-white/70 leading-relaxed">{termData.definition}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Instructional Tooltip - "?" icon with context
export function InstructionalTooltip({ content, children }) {
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button 
            className="inline-flex items-center justify-center w-5 h-5 text-[#0055A5]/60 hover:text-[#0055A5] transition-colors"
            data-testid="instructional-tooltip"
          >
            <HelpCircle className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </TooltipTrigger>
        <TooltipContent 
          side="top" 
          className="bg-[#002B45] text-white border-none rounded-none p-4 max-w-sm shadow-2xl"
        >
          <p className="text-xs leading-relaxed">{content}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Field-level help tooltip
export function FieldHelp({ label, helpText, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-sm font-medium text-[#002B45]">{label}</span>
        <InstructionalTooltip content={helpText} />
      </div>
      {children}
    </div>
  );
}

// Coach Bridge Copy - "Editorial Ribbon" style
export function CoachBridgeCopy({ message }) {
  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="w-full bg-[#0055A5] py-10 sm:py-16 lg:py-20 px-4 sm:px-8 lg:px-16 my-8 sm:my-12"
      data-testid="coach-bridge-copy"
    >
      <div className="max-w-3xl mx-auto text-center">
        <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-6">
          Coach Note
        </p>
        <p className="text-lg sm:text-2xl lg:text-3xl font-headline font-light text-white leading-relaxed">
          {message}
        </p>
      </div>
    </motion.div>
  );
}

// Instructional Fade - dims after interaction
export function InstructionalFade({ children, hasInteracted }) {
  return (
    <p 
      className={`text-sm text-[#475569] mb-6 transition-all duration-700 ${
        hasInteracted ? 'opacity-30 translate-y-1' : 'opacity-100'
      }`}
      data-testid="instructional-fade"
    >
      {children}
    </p>
  );
}

// Dynamic Breadcrumbs
export function DynamicBreadcrumbs({ items, currentIndex }) {
  return (
    <nav 
      className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs mb-6 sm:mb-8"
      data-testid="breadcrumbs"
    >
      {items.map((item, index) => (
        <React.Fragment key={item.id}>
          <span 
            className={`transition-colors ${
              index === currentIndex
                ? 'text-[#002B45] font-semibold'
                : index < currentIndex
                  ? 'text-[#78BE20]'
                  : 'text-[#475569]/40'
            }`}
          >
            {item.label}
          </span>
          {index < items.length - 1 && (
            <span className="text-[#475569]/20">/</span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}

// Why This Matters Panel
export function WhyPanel({ title, content, isOpen, onClose }) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 20 }}
          transition={{ duration: 0.2 }}
          className="fixed right-0 top-0 h-full w-full sm:w-96 bg-[#002B45] text-white shadow-2xl z-50"
          data-testid="why-panel"
        >
          <div className="p-4 sm:p-8 h-full flex flex-col">
            <div className="flex items-center justify-between mb-8">
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#00A9E0]">
                Why This Matters
              </span>
              <button 
                onClick={onClose}
                className="text-white/40 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <h3 className="text-2xl font-headline font-light mb-6">
              {title}
            </h3>
            
            <p className="text-white/60 text-sm leading-relaxed">
              {content}
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Why Toggle Button - opens WhyPanel
export function WhyToggle({ title, content, isOpen, onToggle }) {
  return (
    <>
      <button
        onClick={onToggle}
        className="flex items-center gap-1.5 text-xs text-[#0055A5]/60 hover:text-[#0055A5] transition-colors"
        data-testid="why-toggle"
      >
        <HelpCircle className="w-3.5 h-3.5" strokeWidth={1.5} />
        <span className="uppercase tracking-widest font-medium">
          {isOpen ? 'Close' : 'Why this matters'}
        </span>
      </button>
      <WhyPanel title={title} content={content} isOpen={isOpen} onClose={onToggle} />
    </>
  );
}

// Validation indicator
export function ValidationPulse({ show, children }) {
  return (
    <div 
      className={`transition-all duration-300 ${
        show ? 'ring-2 ring-[#FFB81C] ring-offset-4 ring-offset-white' : ''
      }`}
      data-testid="validation-pulse"
    >
      {children}
    </div>
  );
}

// Section with optional tooltip
export function Section({ title, tooltip, children }) {
  return (
    <section className="mb-12">
      <div className="flex items-center gap-2 mb-4">
        <h2 className="text-lg font-headline font-semibold text-[#002B45]">
          {title}
        </h2>
        {tooltip && <InstructionalTooltip content={tooltip} />}
      </div>
      {children}
    </section>
  );
}

export default InstructionalTooltip;
